import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Activity, DollarSign, Clock, AlertTriangle } from "lucide-react";

type LogRow = {
  id: string;
  step: string;
  provider: string | null;
  model: string | null;
  duration_ms: number | null;
  cost_usd: number | null;
  created_at: string;
};

type Agg = {
  total_turns: number;
  total_cost: number;
  avg_voice_latency: number;
  bypass_count: number;
};

export function JarvisObservabilityTab() {
  const [agg, setAgg] = useState<Agg | null>(null);
  const [recent, setRecent] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      const [turnsRes, logsRes, bypassRes] = await Promise.all([
        supabase.from("jarvis_session_turns")
          .select("id, latency_total_ms")
          .gte("created_at", since),
        supabase.from("jarvis_multimodal_log")
          .select("id, step, provider, model, duration_ms, cost_usd, created_at")
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(20),
        supabase.from("jarvis_multimodal_log")
          .select("cost_usd")
          .gte("created_at", since),
      ]);

      if (cancelled) return;
      const turns = (turnsRes.data ?? []) as Array<{ latency_total_ms: number | null }>;
      const logs = (logsRes.data ?? []) as LogRow[];
      const allLogs = (bypassRes.data ?? []) as Array<{ cost_usd: number | null }>;
      const totalCost = allLogs.reduce((s, l) => s + Number(l.cost_usd ?? 0), 0);
      const voiceLatencies = logs
        .filter(l => l.step === "tts" || l.step === "stt")
        .map(l => l.duration_ms ?? 0)
        .filter(n => n > 0);
      const avgVoice = voiceLatencies.length ? voiceLatencies.reduce((a, b) => a + b, 0) / voiceLatencies.length : 0;

      // Bypass = LLM gateway logs (model_decisions) sem entrada equivalente em jarvis_multimodal_log.
      // Para simplicidade aqui, mostramos contagem da view se existir.
      let bypassCount = 0;
      try {
        const { count } = await (supabase as unknown as {
          from: (t: string) => { select: (c: string, opts: { count: "exact"; head: true }) => Promise<{ count: number | null }> };
        }).from("v_jarvis_text_bypass_check").select("*", { count: "exact", head: true });
        bypassCount = count ?? 0;
      } catch { /* view pode não existir */ }

      setAgg({
        total_turns: turns.length,
        total_cost: totalCost,
        avg_voice_latency: avgVoice,
        bypass_count: bypassCount,
      });
      setRecent(logs);
      setLoading(false);
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  if (loading || !agg) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold font-headline">Observabilidade — últimas 24h</h2>
        <p className="text-xs text-muted-foreground">Custo, latência e auditoria de gateway.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Activity className="w-3 h-3" /> Turnos</div>
          <div className="text-2xl font-semibold tabular-nums">{agg.total_turns}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="w-3 h-3" /> Custo</div>
          <div className="text-2xl font-semibold tabular-nums">${agg.total_cost.toFixed(3)}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Latência voz (avg)</div>
          <div className="text-2xl font-semibold tabular-nums">{agg.avg_voice_latency.toFixed(0)}ms</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Bypass gateway</div>
          <div className="text-2xl font-semibold tabular-nums">{agg.bypass_count}</div>
        </Card>
      </div>

      {agg.bypass_count > 0 && (
        <Card className="p-3 border-destructive/40 bg-destructive/5">
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-destructive" />
            <span className="font-semibold">{agg.bypass_count} chamada(s) LLM fora do gateway</span> — auditar.
          </div>
        </Card>
      )}

      <Card className="p-3">
        <div className="text-sm font-semibold mb-2">Logs multimodais recentes</div>
        <div className="space-y-1 text-xs">
          {recent.map(r => (
            <div key={r.id} className="flex items-center gap-2 py-1 border-b border-border/30 last:border-0">
              <Badge variant="outline" className="text-[10px] uppercase">{r.step}</Badge>
              <span className="text-muted-foreground">{r.provider}/{r.model}</span>
              <span className="ml-auto tabular-nums">{r.duration_ms ?? 0}ms</span>
              <span className="tabular-nums text-muted-foreground">${Number(r.cost_usd ?? 0).toFixed(4)}</span>
            </div>
          ))}
          {recent.length === 0 && <div className="text-muted-foreground py-4 text-center">Sem atividade.</div>}
        </div>
      </Card>
    </div>
  );
}
